/**
 * Classificação de erro, compartilhada por leitura, escrita e envio da fila.
 *
 * As três precisam responder à mesma pergunta — "dá para tentar de novo?" — e
 * cada uma respondia do seu jeito. Aqui elas concordam.
 */

/**
 * Sessão vencida ou inválida (HTTP 401).
 *
 * É diferente de permissão negada: RLS barrando devolve 403 e nunca vai passar
 * por mais que se tente. 401 passa assim que o usuário entrar de novo, então é
 * espera, não recusa.
 */
export const isAuthError = (err) => {
  if (!err) return false;
  if (err.status === 401 || err.statusCode === 401) return true;
  // PGRST301 = JWT expirado; PGRST302 = JWT ausente.
  if (err.code === 'PGRST301' || err.code === 'PGRST302') return true;
  return /jwt (expired|is invalid)|invalid (jwt|token)|not authenticated/i.test(err.message || '');
};

/** Não chegou no servidor: sem rede, DNS, servidor fora. */
export const isNetworkError = (err) => {
  // O formato do erro vem primeiro. Se o servidor RESPONDEU — com código de
  // erro do Postgres ou negando a sessão —, houve rede, por definição.
  // Perguntar o estado da conexão antes disso classificaria uma recusa
  // legítima como queda, e ela iria para a fila para nunca ser aceita.
  if (isAuthError(err)) return false;
  if (err?.code) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return err instanceof TypeError
    || /fetch|network|failed to fetch/i.test(err?.message || '');
};

/**
 * Vale a pena tentar de novo mais tarde?
 *
 * Sim para falta de rede e para sessão vencida. Não para o resto: dado
 * inválido ou permissão negada dariam o mesmo erro para sempre, e reenviar em
 * laço só esconderia o problema de quem precisa resolvê-lo.
 */
export const isRetryable = (err) => isNetworkError(err) || isAuthError(err);

export const SESSION_EXPIRED_MESSAGE =
  'Sua sessão expirou. Entre novamente para continuar — o que você lançou está guardado.';

/**
 * Mensagem para mostrar ao usuário.
 *
 * Sessão vencida e falta de conexão têm resposta pronta e uma ação clara. O
 * resto passa a mensagem do servidor, que é feia mas verdadeira — melhor que
 * um texto genérico que esconde o que houve.
 */
export const describeError = (err) => {
  if (err?.sessionExpired || isAuthError(err)) return SESSION_EXPIRED_MESSAGE;
  if (isNetworkError(err)) return 'Sem conexão com o servidor. Tente de novo quando o sinal voltar.';
  return err?.message || 'Erro desconhecido.';
};
